import React from 'react';
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';

interface AvatarSyncState {
  // Estado global para sincronização de avatar
  avatarUpdateTrigger: number;
  pendingAvatarUrl: string | null;
  currentAvatarUrl: string | null;

  // User information for fallback
  userName: string | null;
  userEmail: string | null;

  // Loading and error states
  isLoading: boolean;
  error: string | null;

  // Actions
  triggerAvatarUpdate: (newUrl?: string) => void;
  clearPendingAvatar: () => void;
  forceRefreshAllAvatars: () => void;
  setAvatarUrl: (url: string | null) => void;
  setUser: (name: string | null, email: string | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  reset: () => void;
}

export const useAvatarSync = create<AvatarSyncState>()(
  subscribeWithSelector((set, get) => ({
    avatarUpdateTrigger: 0,
    pendingAvatarUrl: null,
    currentAvatarUrl: null,
    userName: null,
    userEmail: null,
    isLoading: false,
    error: null,

    triggerAvatarUpdate: (newUrl?: string) => {
      set((state) => ({
        avatarUpdateTrigger: state.avatarUpdateTrigger + 1,
        pendingAvatarUrl: newUrl || state.pendingAvatarUrl,
        error: null,
      }));

      // Limpa o pending após um curto período
      setTimeout(() => {
        set({ pendingAvatarUrl: null });
      }, 1000);
    },

    clearPendingAvatar: () => {
      set({ pendingAvatarUrl: null });
    },

    forceRefreshAllAvatars: () => {
      // Incrementa trigger para forçar re-render de todos os avatares
      set((state) => ({
        avatarUpdateTrigger: state.avatarUpdateTrigger + 1,
      }));
    },

    setAvatarUrl: (url: string | null) => {
      const currentUrl = get().currentAvatarUrl;
      const currentBaseUrl = currentUrl ? currentUrl.split('?')[0] : null;
      const newBaseUrl = url ? url.split('?')[0] : null;
      
      // Só atualiza se a URL base mudou
      if (currentBaseUrl !== newBaseUrl) {
        const urlWithCacheBusting = addCacheBusting(url);
        set({ currentAvatarUrl: urlWithCacheBusting, error: null });
        // Automaticamente trigger update para notificar todos os componentes
        get().triggerAvatarUpdate(urlWithCacheBusting);
      }
      // Se a URL base é a mesma, não faz nada (evita loops)
    },

    setUser: (name: string | null, email: string | null) => {
      set({ userName: name, userEmail: email });
    },

    setLoading: (loading: boolean) => {
      set({ isLoading: loading });
    },

    setError: (error: string | null) => {
      set({ error });
    },

    reset: () => {
      set({
        avatarUpdateTrigger: 0,
        pendingAvatarUrl: null,
        currentAvatarUrl: null,
        userName: null,
        userEmail: null,
        isLoading: false,
        error: null,
      });
    },
  }))
);

/**
 * Utilitário para adicionar timestamp que quebra cache do navegador
 * Impede que o navegador mostre imagens antigas do avatar
 * SEMPRE limpa URLs antigas e adiciona novo timestamp para garantir consistência
 */
function addCacheBusting(url: string | null): string | null {
  if (!url) return null;

  // Limpa TODOS os parâmetros existentes para evitar duplicação
  // Isso remove ?t=, ?v=, &_cb= e qualquer outro parâmetro
  const cleanUrl = url.split('?')[0];

  // Adiciona timestamp único para quebrar cache do navegador
  return `${cleanUrl}?t=${Date.now()}`;
}

// Hook principal para gerenciamento de avatar
export function useAvatarManager() {
  const state = useAvatarSync();

  /**
   * Atualiza avatar com cache-busting automático e notificação global
   * Evita atualizações duplicadas quando a URL base é a mesma
   */
  const updateAvatar = React.useCallback((url: string | null) => {
    try {
      const currentUrl = state.currentAvatarUrl;
      const currentBaseUrl = currentUrl ? currentUrl.split('?')[0] : null;
      const newBaseUrl = url ? url.split('?')[0] : null;
      
      // Só atualiza se a URL base mudou
      if (currentBaseUrl !== newBaseUrl) {
        console.log('✅ Avatar updated (base URL changed):', {
          previousBase: currentBaseUrl,
          newBase: newBaseUrl
        });
        // setAvatarUrl já adiciona cache-busting automaticamente
        state.setAvatarUrl(url);
      } else {
        // URL base é a mesma, não faz nada para evitar loops
        // O cache-busting existente já é suficiente
      }
    } catch (error) {
      console.error('Error updating avatar:', error);
      state.setError('Failed to update avatar');
    }
  }, [state.setAvatarUrl, state.setError, state.currentAvatarUrl]);

  /**
   * Obtém iniciais para fallback quando não há imagem
   */
  const getInitials = (): string => {
    if (state.userName) {
      return state.userName
        .split(' ')
        .map(part => part.charAt(0).toUpperCase())
        .join('')
        .slice(0, 2);
    }
    if (state.userEmail) {
      return state.userEmail.charAt(0).toUpperCase();
    }
    return '?';
  };

  /**
   * Gera chave única para forçar re-render completo do componente
   * Usado como React key prop para garantir re-mount completo quando necessário
   */
  const getAvatarKey = (): string => {
    return `avatar-${state.avatarUpdateTrigger}-${state.currentAvatarUrl || 'no-avatar'}`;
  };

  /**
   * Atualiza manualmente o avatar (útil para troubleshooting)
   */
  const refreshAvatar = React.useCallback(() => {
    try {
      if (state.currentAvatarUrl) {
        // Força novo timestamp
        const baseUrl = state.currentAvatarUrl.split('?t=')[0];
        updateAvatar(baseUrl);
      } else {
        state.forceRefreshAllAvatars();
      }
    } catch (error) {
      console.error('Error refreshing avatar:', error);
      state.setError('Failed to refresh avatar');
    }
  }, [state.currentAvatarUrl, updateAvatar, state.forceRefreshAllAvatars, state.setError]);

  return {
    // Estado
    ...state,

    // Computados
    initials: getInitials(),
    avatarKey: getAvatarKey(),

    // Ações aprimoradas
    updateAvatar,
    refreshAvatar,
    addCacheBusting,
  };
}

// Hook para componentes ouvirem atualizações de avatar (mantém compatibilidade)
export function useAvatarListener(avatarUrl?: string | null) {
  const avatarUpdateTrigger = useAvatarSync((state) => state.avatarUpdateTrigger);
  const pendingAvatarUrl = useAvatarSync((state) => state.pendingAvatarUrl);

  // Se há um avatar pendente que corresponde a este componente, retorna ele
  if (pendingAvatarUrl && avatarUrl) {
    // Compara URLs sem timestamp
    const cleanAvatarUrl = avatarUrl.split('?')[0];
    const cleanPendingUrl = pendingAvatarUrl.split('?')[0];

    if (cleanAvatarUrl === cleanPendingUrl) {
      return pendingAvatarUrl;
    }
  }

  return avatarUrl;
}

/**
 * Hook para sincronizar avatar com dados do perfil
 * Atualiza automaticamente quando o perfil muda
 */
export function useProfileAvatarSync(profile: {
  avatar_url?: string | null;
  full_name?: string | null;
  email?: string | null;
} | null) {
  const { updateAvatar, setUser, currentAvatarUrl } = useAvatarManager();
  
  // Usar ref para rastrear a última URL base processada (sem cache-busting)
  // Isso evita chamadas duplicadas quando a URL base é a mesma
  const lastProcessedUrlRef = React.useRef<string | null>(null);

  // Sincroniza URL do avatar quando perfil muda
  React.useEffect(() => {
    try {
      const profileAvatarUrl = profile?.avatar_url;
      
      // Extrai a URL base (sem cache-busting) para comparação
      const baseUrl = profileAvatarUrl ? profileAvatarUrl.split('?')[0] : null;
      
      // Só atualiza se a URL base mudou desde a última vez
      if (baseUrl !== lastProcessedUrlRef.current) {
        if (profileAvatarUrl) {
          // CRÍTICO: Sempre aplicar cache-busting ao carregar do perfil
          // Isso garante que após refresh, a imagem mais recente seja carregada
          console.log('🔄 Syncing avatar from profile (base URL changed):', baseUrl);
          lastProcessedUrlRef.current = baseUrl;
          updateAvatar(profileAvatarUrl);
        } else {
          // Limpa avatar se perfil não tem mais
          if (lastProcessedUrlRef.current !== null) {
            console.log('🔄 Clearing avatar (profile has no avatar)');
            lastProcessedUrlRef.current = null;
            updateAvatar(null);
          }
        }
      }
      // Se a URL base é a mesma, não faz nada (evita loops)
    } catch (error) {
      console.error('Error in useProfileAvatarSync useEffect:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // updateAvatar e currentAvatarUrl são estáveis, não precisam estar nas dependências
  }, [profile?.avatar_url]);

  // Sincroniza informações do usuário quando perfil muda
  React.useEffect(() => {
    if (profile) {
      setUser(
        profile.full_name || null,
        profile.email || null
      );
    }
  }, [profile?.full_name, profile?.email, setUser]);

  return useAvatarListener(profile?.avatar_url);
}

// Export validation - ensure all avatar sync functions are available
export const avatarSyncExports = {
  useAvatarSync,
  useAvatarManager,
  useAvatarListener,
  useProfileAvatarSync,
} as const;

// Re-export for explicit availability
export { useAvatarSync as default };